import axios from "../../axios";

// NOTE: baseURL already ends with '/api/'.
// Use path without leading slash so requests resolve to `${BASE_URL}v2/...`.
const BASE = "v2/researchers";

export const getResearcherProfile = async (userId?: number) => {
  const url = `${BASE}/${userId}/profile`;
  const { data } = await axios.get(url);
  return data;
};

export const getResearcherAchievements = async (userId?: number) => {
  const { data } = await axios.get(`${BASE}/${userId}/achievements`);
  return data;
};

export const getResearcherActivity = async (
  userId: number | undefined,
  params: { limit?: number; offset?: number } = {}
) => {
  const { data } = await axios.get(`${BASE}/${userId}/activity`, {
    params
  });
  return data;
};

export const listResearcherCertifications = async (userId?: number) => {
  const { data } = await axios.get(`${BASE}/${userId}/certifications`);
  return data;
};

export const createResearcherCertification = async (
  payload: {
    certification_name: string;
    issuing_organization: string;
    issue_date: string;
    expiry_date?: string | null;
  },
  userId?: number
) => {
  const { data } = await axios.post(`${BASE}/${userId}/certifications`, payload);
  return data;
};

export const deleteResearcherCertification = async (
  certificationId: number,
  userId?: number
) => {
  const { data } = await axios.delete(
    `${BASE}/${userId}/certifications/${certificationId}`
  );
  return data;
};

export const getResearcherRank = async (userId?: number) => {
  const { data } = await axios.get(`${BASE}/${userId}/rank`);
  return data;
};

// Open SLA-breached retests for dashboard alerts
export const getOpenSlaBreaches = async (userId?: number) => {
	if (!userId) return { items: [] as Array<any> };
	const { data } = await axios.get(`${BASE}/${userId}/open-sla-breaches`);
	return data as {
	  items: Array<{
	    retest_id: number;
	    report_id: number;
	    report_title?: string | null;
	    program_id?: number | null;
	    program_name?: string | null;
	    severity?: number | null;
	    severity_category?: string | null;
	    sla_deadline_at?: string | null;
	    sla_breached_at?: string | null;
	    alertType?: "warning" | "breached";
	    hoursLeft?: number;
	  }>;
	};
};

// SLA breach history for dashboard section
export const getSlaBreaches = async (userId?: number) => {
	if (!userId) return { items: [] as Array<any>, penaltyPerBreach: 0, totalPenaltyPoints: 0 };
	const { data } = await axios.get(`${BASE}/${userId}/sla-breaches`);
	return data as {
	  items: Array<{
	    retest_id: number;
	    report_id: number;
	    report_title?: string | null;
	    program_id?: number | null;
	    program_name?: string | null;
	    severity?: number | null;
	    severity_category?: string | null;
	    sla_deadline_at?: string | null;
	    sla_breached_at?: string | null;
	    status?: string | null;
	    penaltyPoints: number;
	  }>;
	  penaltyPerBreach: number;
	  totalPenaltyPoints: number;
	};
};

// Retests
export const getReportDetailsByRetestId = async (retestId: number) => {
  // Note: baseURL already includes '/api/'
  const { data } = await axios.get(`v2/retests/${retestId}/report-details`);
  // Backend responds with { message, data: { title, severity_category, ... } }
  const payload = data?.data || data || {};
  return {
    title: payload.title || payload.report_title,
    severity: payload.severity_category || payload.severity_label || payload.severity
  } as { title?: string; severity?: string };
};

// Dashboard overview (KPIs)
export const getResearchersOverview = async () => {
  const { data } = await axios.get(`${BASE}/overview`);
  return data as {
    totals: {
      totalResearchers: number;
      totalVulnerabilities: number;
      criticalFindings: number;
    };
    avgResponseTimeHours: number;
    severityDistribution: { critical: number; high: number; medium: number; low: number };
    vulnerabilityTypes: any;
  };
};

// Monthly activity for last N months
export const getResearchersMonthlyActivity = async (months = 12) => {
  const { data } = await axios.get(`${BASE}/overview/monthly-activity`, {
    params: { months }
  });
  return data as { series: Array<{ month: string; count: number }> };
};

export const getResearchersLeaderboard = async (
  params: {
    page?: number;
    pageSize?: number;
    q?: string;
    approved?: boolean;
    specialization?: string;
    status?: string;
  } = {}
) => {
  const { data } = await axios.get(`${BASE}/leaderboard`, { params });
  return data as {
    items: Array<{
      rank: number;
      user_id: number;
      name: string;
      pfp?: string | null;
      joinedAt?: string | null;
      country?: string | null;
      approved: boolean;
	      reputation: number; // adjusted reputation after SLA penalties
	      baseReputation?: number; // raw reputation before SLA
	      adjustedReputation?: number; // explicit adjusted reputation (same as reputation)
	      slaPenaltyPoints?: number; // total points deducted due to SLA breaches
      totalReports: number;
      verifiedReports: number;
      pendingReports: number;
      criticalFinds: number;
      specialization?: string | null;
      status?: string | null;
    }>;
    total: number;
    page: number;
    pageSize: number;
    nextOffset: number;
  };
};

export const getTopPrograms = async () => {
  const { data } = await axios.get(`${BASE}/top-programs`);
  return data as {
    programs: Array<{
      program_id: number;
      name: string;
      members: number;
      totalValid: number;
      criticalValid: number;
    }>;
  };
};


